import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { openRecipeDetail } from '../../../../utils/recipeNavigation';
import { Paprika } from '../../../../styles/theme';
import { MEALS, SIDE_REASONS, slotKey, rowOf, dayOf, dayFullKey, DAY_PROFILE, shiftWeek } from '../constants';

// Breakfast is only valid for drop on weekends (dayOf >= 5); weekday breakfast cells reject drops
function isExcludedFromDrop(sk: number): boolean {
  return rowOf(sk) === 0 && dayOf(sk) < 5;
}
import { Toast } from '../primitives/Toast';
import { SlotGrid } from './SlotGrid';
import { SlotMenu } from './SlotMenu';
import { WeekSummaryLine } from './WeekSummaryLine';
import { ReadingCard } from '../../../onboarding/ReadingCard';
import { WeekCounterBanner } from '../../WeekCounterBanner';
import { NextMealBar, NextEntryBar } from './NextMealBar';
import { findNextMealTarget, findNextEntryTarget, pickBarTarget, nowAnchor } from '../nextMeal';
import { placedInSlotMessage, slotLabel } from '../slotPlacementToast';
import { mealStatesForDay, defaultMealForDay } from '../dayAddTarget';
import { addDays, currentWeekStart, dayIndexInWeek } from '../../../../utils/localDate';
import { ReplaceDrawer, type FanOption } from './ReplaceDrawer';
import { RecipeDetailDrawer } from './RecipeDetailDrawer';
import { CookCapture, type CookCaptureEntry } from '../../../recipe-detail/CookLog';
import { useTranslation } from 'react-i18next';
import { useSameMealExclude } from '../../../../hooks/useSameMealExclude';
import { useSlotFit } from '../../../../hooks/useSlotFit';
import { usePrewarmPantryMatcher } from '../../../../hooks/usePrewarmPantryMatcher';
import { usePrefetchPlannedRecipes } from '../../../../hooks/usePrefetchPlannedRecipes';
import { prefetchRecipe } from '../../../../services/recipePrefetch';
import { DISQUALIFYING_SCORE } from '../../../../services/planRanking';
import { useHouseholdMembers } from '../../../../hooks/useHouseholdMembers';
import { slotHasAcceptedMeal } from '../../proposalState';
import { LibraryDrawer } from './LibraryDrawer';
import { EntryFormModal } from './EntryFormModal';
import { LeftoverPickerModal } from './LeftoverPickerModal';
import { useUndoableWeek } from '../useUndoableWeek';
import { useGenerateWeek } from '../../../meal-config/useGenerateWeek';
import { isEntryMeal, isRecipeMeal, isSideableEntryMeal } from '../../../../redux/mealEntry';
import {
  useLeftoverCandidates,
  usePendingCapture,
  usePendingLeftovers,
  todayIso,
} from '../useLeftoverCandidates';
import { useLeftoverPlacement } from '../useLeftoverPlacement';
import { LeftoverCapturePrompt } from '../LeftoverCapturePrompt';
import { LeftoverPendingBanner } from '../LeftoverPendingBanner';
import type { LeftoverCandidate } from '../leftovers';
import { backfillIndexFromMeals, localizeIndex } from '../mealIndexFallback';
import { selectCurrentLanguage } from '../../../../redux/languageSlice';
import { buildSlotMeals, canAddDish, primarySlotMap, slotKeyOfMeal, MI_BY_MEAL_TYPE } from '../slotMeals';
import { useSideSuggestions, type SideOption } from '../useSideSuggestions';
import { useSideFoodPicks } from '../sideFoodsState';
import { trackEvent } from '../../../../services/analyticsService';
import { selectPlannerFocusRow, setPlannerFocusRow } from '../../../../redux/navigationSlice';
import { effectiveFocusRow, type FocusRow } from './rowFocus';
import { buildMovePlan, dayIndexOf, type MoveAction } from '../../../../services/mealMovePlan';
import { AddToMealPlanModal } from '../../../recipe-detail/AddToMealPlanModal';
import {
  setSel,
  setLibFor,
  setDetailFor,
  setFlash,
  setMoving,
  clearToast,
  setToast,
  setConfigPhase,
  selectSel,
  selectMoving,
  selectLibFor,
  selectDetailFor,
  selectFlash,
  selectToast,
  selectHomeRev,
} from '../../../../redux/mealPlanUISlice';
import {
  selectCurrentWeekMeals,
  selectCurrentWeekMealsWithProposals,
  selectCurrentWeekStart,
  selectMealsForWeek,
  setCurrentWeek,
  removeFromWeek,
  addToWeek,
  setSlotEntry,
  clearSlot,
  swapSlots,
  moveMeal,
  getWeekStartForDate,
  selectMealsForDateRange,
  markMealAsCooked,
  unmarkMealAsCooked,
  rateMeal,
  proposeMeal,
  selectPlannerHorizonMeals,
  type PlannedMeal,
} from '../../../../redux/mealPlanSlice';
import { selectRecipeIndex } from '../../../../redux/recipeIndexSlice';
import type { RootState } from '../../../../redux/store';
import { selectActiveInventory } from '../../../../redux/inventorySlice';
import { useCookPreferences } from '../../../../hooks/useCookPreferences';
import { selectAllRecipeMetadata } from '../../../../redux/recipeMetadataSlice';
import { useVerdictPrompt } from '../../../household/VerdictPromptProvider';
import { useFanChoice, type FanChoiceParams } from '../useFanChoice';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import type { DietaryRole, PlannedMeal as PlannedMealType } from '../../../../redux/mealPlanSlice';
import { formatWeekRange, formatDayMonth } from '../../../../utils/dateLocale';
import { canHover } from '../../../../utils/platformDetection';





function entryInitialFor(meal: PlannedMeal, type: 'dining' | 'note'): { place?: string; note?: string; text?: string } | null {
  if (!meal.entry || meal.entry.type !== type) return null;
  if (type === 'dining') {
    return { place: (meal.entry as any).place, note: (meal.entry as any).note };
  }
  return { text: (meal.entry as any).text };
}

interface Anchor {
  x: number;
  y: number;
  width: number;
  height: number;
}

type MenuKey = 'recipe' | 'dining' | 'leftover' | 'note' | 'skip' | 'clear';

export function WeeklyDesktop() {
  const dispatch = useDispatch();
  const { openVerdict } = useVerdictPrompt();
  const store = useStore<RootState>();
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const { status: householdStatus, myUid } = useHouseholdMembers();

  const weekMeals = useSelector(selectCurrentWeekMeals);
  const weekMealsWithProposals = useSelector(selectCurrentWeekMealsWithProposals);
  const weekStart = useSelector(selectCurrentWeekStart);
  const recipeIndexState = useSelector(selectRecipeIndex);
  const activeInventory = useSelector(selectActiveInventory);
  const prefs = useCookPreferences();
  const recipeMetadata = useSelector(selectAllRecipeMetadata);

  const sel = useSelector(selectSel);
  const moving = useSelector(selectMoving);
  const libFor = useSelector(selectLibFor);
  // One row of the grid is open, the other two are strips (rowFocus.ts). The
  // choice is the cook's and is kept on the device; the Library drawer opens
  // the row it is filling without rewriting it.
  const storedFocusRow = useSelector(selectPlannerFocusRow);
  const focusRow = effectiveFocusRow(storedFocusRow, libFor);
  const handleFocusRow = useCallback((row: FocusRow, via: 'card' | 'label' | 'add' | 'drag') => {
    if (row === focusRow) return;
    dispatch(setPlannerFocusRow(row));
    trackEvent('planner_row_opened', { row: MEALS[row].mealType, from: MEALS[focusRow].mealType, via });
  }, [dispatch, focusRow]);
  const detailFor = useSelector(selectDetailFor);
  const flash = useSelector(selectFlash);
  const toast = useSelector(selectToast);
  const homeRev = useSelector(selectHomeRev);

  // Cook-capture sheet — the PlannedMeal the user is rating/noting (null = closed)
  const [cookCaptureMeal, setCookCaptureMeal] = useState<PlannedMeal | null>(null);
  // Local state for slot menu
  const [slotMenu, setSlotMenu] = useState<{ sk: number; anchor: Anchor } | null>(null);
  // Entry form modal state
  const [entryForm, setEntryForm] = useState<{
    sk: number;
    type: 'dining' | 'note';
    initial?: { place?: string; note?: string; text?: string } | null;
  } | null>(null);
  // Leftover picker modal state
  const [leftoverPick, setLeftoverPick] = useState<{ sk: number } | null>(null);
  // The dish the move overlay is relocating (#563 follow-up). Opened from the
  // detail drawer, so touch hardware on the desktop layout has a path that
  // isn't HTML5 drag.
  const [moveCtx, setMoveCtx] = useState<{ mealId: string } | null>(null);
  // The drawer is listing accompaniments for the selected slot's dish (#746)
  // rather than dinners to replace it with. Outlives the drawer while the
  // Library it opened is still choosing, so that pick is added too.
  const [drawerSides, setDrawerSides] = useState(false);
  // Flash timeout tracking
  const flashTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // « Mes repas » pressed on the planner: close what this screen keeps itself.
  useEffect(() => {
    if (homeRev === 0) return;
    setSlotMenu(null);
    setEntryForm(null);
    setLeftoverPick(null);
    setMoveCtx(null);
    setDrawerSides(false);
    setCookCaptureMeal(null);
  }, [homeRev]);

  // Undo hook
  const { mutateWithUndo, undo } = useUndoableWeek();

  // Generate week hook
  const { generate: generateWeek } = useGenerateWeek();

  // Cleanup flash timeout on unmount
  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) {
        clearTimeout(flashTimeoutRef.current);
      }
    };
  }, []);

  const language = useSelector(selectCurrentLanguage);
  const recipeMap = useMemo<Record<string, RecipeIndexEntry>>(() => {
    const map: Record<string, RecipeIndexEntry> = {};
    recipeIndexState.forEach((r: RecipeIndexEntry) => { map[r.id] = r; });
    // Meals whose recipe isn't in the index would render as empty slots —
    // fall back to the meal's denormalized recipe so the card stays visible.
    // Use weekMealsWithProposals so proposals don't drop out of the index.
    // ...and every title in the cook's language (#736).
    return localizeIndex(backfillIndexFromMeals(map, weekMealsWithProposals), weekMealsWithProposals, language);
  }, [recipeIndexState, weekMealsWithProposals, language]);

  // Build slotKey → every meal planned in that slot (a slot can hold a main
  // plus a side). Keyed one-per-slot, extra dishes were silently dropped.
  // Use weekMealsWithProposals so proposals render in the grid.
  const slotMeals = useMemo(() => buildSlotMeals(weekMealsWithProposals), [weekMealsWithProposals]);

  // Primary dish per slot — drag-and-drop, swap and leftovers act on it.
  const slotsMap = useMemo<Map<number, PlannedMeal>>(() => primarySlotMap(slotMeals), [slotMeals]);

  // Leftovers are chosen by date, not by position in the grid on screen (#663).
  const today = todayIso();
  const leftoverTargetDate = leftoverPick ? addDays(weekStart, dayOf(leftoverPick.sk)) : null;
  const { fridge: leftoverFridge, freezer: leftoverFreezer } =
    useLeftoverCandidates(leftoverTargetDate);
  const placeLeftover = useLeftoverPlacement(t);
  const captureMeal = usePendingCapture(today);
  const pendingLeftovers = usePendingLeftovers(today);

  const recipeEntries = useMemo<RecipeIndexEntry[]>(
    () => recipeIndexState,
    [recipeIndexState],
  );

  /**
   * The dish the bar answers with (#579). Same selection as mobile's hero, so
   * the two platforms can't disagree on what "next" means. Null when the week
   * on screen isn't the current one, or nothing is left to cook — the bar hides
   * and the full grocery banner comes back.
   */
  const nextMeal = useMemo(() => {
    const { today, nowMinutes } = nowAnchor();
    const target = findNextMealTarget({
      slotMeals,
      weekStart,
      today,
      nowMinutes,
      resolves: (recipeId) => !!recipeMap[recipeId],
    });
    if (!target) return null;

    const todayDi = dayIndexInWeek(weekStart, today);
    if (todayDi === null) return null;

    return { ...target, recipe: recipeMap[target.meal.recipeId], todayDi };
  }, [slotMeals, recipeMap, weekStart]);

  /**
   * The next simple meal, restaurant or leftovers. When it comes before the
   * next recipe, the bar shows it instead: a meal is planned even when there
   * is nothing to cook, and the bar is where the cook checks.
   */
  const barEntry = useMemo(() => {
    const { today, nowMinutes } = nowAnchor();
    const entry = findNextEntryTarget({ slotMeals, weekStart, today, nowMinutes });
    const picked = pickBarTarget(nextMeal, entry);
    if (picked?.kind !== 'entry') return null;
    const todayDi = dayIndexInWeek(weekStart, today);
    return todayDi === null ? null : { ...picked.target, todayDi };
  }, [slotMeals, weekStart, nextMeal]);

  // Fan alternatives for the selected slot. The seed is renewed whenever the
  // drawer opens on a slot (sel transitions), so each opening shows a fresh
  // mix while the list stays stable during a single opening. Derived during
  // render (not in an effect) so the very first paint already uses the seed.
  const sameMealExclude = useSameMealExclude();
  const slotFitOf = useSlotFit();
  const fanSeedRef = useRef(0);
  const prevSelRef = useRef<number | null>(null);
  if (sel !== prevSelRef.current) {
    prevSelRef.current = sel;
    if (sel !== null) fanSeedRef.current = Date.now();
  }
  const fanSeed = fanSeedRef.current;

  // The library × shelf walk every ranking below reads, done off the tap (#865).
  const historyRecipes = useMemo(
    () => weekMealsWithProposals.flatMap((m) => (isRecipeMeal(m) && m.recipe?.title ? [m.recipe] : [])),
    [weekMealsWithProposals],
  );
  usePrewarmPantryMatcher(recipeEntries, activeInventory, { history: historyRecipes, score: prefs.score });
  // Today's and tomorrow's recipes, read before they are opened (#874). The
  // horizon, not the week: on a Sunday, tomorrow is in the next bucket.
  const horizonMeals = useSelector(selectPlannerHorizonMeals);
  usePrefetchPlannedRecipes(horizonMeals);
  const sideSuggester = useSideSuggestions(recipeMap);
  const { forSlot: sidesForSlot, topForSlot: topSideForSlot } = sideSuggester;
  useEffect(() => {
    if (sel === null && libFor === null) setDrawerSides(false);
  }, [sel, libFor]);

  // The foods the cook asked the accompaniment list to use (#853).
  const { sideFoods, withFoods } = useSideFoodPicks({
    suggester: sideSuggester,
    slot: sel !== null ? slotMeals.get(sel) : undefined,
    active: drawerSides && sel !== null,
    openingKey: fanSeed,
    surface: 'desktop_drawer',
  });

  // What the ranking reads of the week, as values (#865): live sync recreates
  // the meal objects, and a ranking keyed on them re-ran on every echo.
  const fanTargetId = sel !== null ? slotsMap.get(sel)?.id : undefined;
  const fanTargetRecipeId = sel !== null ? slotsMap.get(sel)?.recipeId : undefined;
  const placedKey = useMemo(() => {
    const ids: string[] = [];
    slotMeals.forEach((meals) => meals.forEach((m) => ids.push(m.recipeId)));
    return ids.join('\u0000');
  }, [slotMeals]);

  // What the fan may offer this slot. Null while it lists accompaniments.
  const fanParams = useMemo<FanChoiceParams | null>(() => {
    if (sel === null || drawerSides) return null;
    const placedRecipeIds = placedKey ? placedKey.split('\u0000') : [];
    const slotDate = addDays(weekStart, dayOf(sel));
    const repeats = sameMealExclude(slotDate, fanTargetId);
    return {
      sk: sel,
      currentRecipeId: fanTargetRecipeId,
      allRecipes: recipeEntries,
      placedRecipeIds,
      inventory: activeInventory,
      metadataById: recipeMetadata,
      // Same hard rules the week generator obeys — allergies, excluded
      // ingredients and declared diets, from the one resolver.
      // ...and never a dish planned within two weeks of this day (#736).
      exclude: (r) => (prefs.hasHardRules && prefs.isExcluded(r)) || repeats(r),
      // ...and, for a dinner, the day's kind and time cap and the week's
      // balance — the rules the generators placed the week by.
      slotFit: rowOf(sel) === 2 ? slotFitOf(slotDate, fanTargetId) : undefined,
      viable: rowOf(sel) === 2
        ? (r) => prefs.score(r, { isWeekend: dayOf(sel) >= 5 }) > DISQUALIFYING_SCORE
        : undefined,
    };
  }, [sel, drawerSides, recipeEntries, placedKey, fanTargetId, fanTargetRecipeId, activeInventory, recipeMetadata, prefs, sameMealExclude, slotFitOf, weekStart]);
  const fanChoice = useFanChoice({ params: fanParams, openingKey: fanSeed, surface: 'desktop_drawer' });
  const { rows: fanRows } = fanChoice;

  const fanOptions = useMemo<FanOption[]>(() => {
    if (sel === null) return [];
    if (drawerSides && withFoods) {
      return withFoods.options.map((s) => ({
        key: `side-food-${sel}-${s.recipe.id}`,
        recipe: s.recipe,
        consequence: t(s.reasonKey, s.reasonParams),
        reason: { accent: SIDE_REASONS[0].accent },
      }));
    }
    if (drawerSides) {
      return sidesForSlot(slotMeals.get(sel), weekMealsWithProposals, fanSeed).map((s) => ({
        key: `side-${sel}-${s.recipe.id}`,
        recipe: s.recipe,
        consequence: t(s.reasonKey, s.reasonParams),
        reason: { labelKey: s.labelKey, accent: s.accent },
      }));
    }
    return fanRows.map((row) => ({
      key: `fan-${sel}-${row.recipe.id}`,
      recipe: row.recipe,
      consequence: row.reasonKey ? t(row.reasonKey, row.reasonParams) : undefined,
      angleKey: row.angleKey,
      reason: row.accent ? { accent: row.accent } : undefined,
    }));
  }, [sel, drawerSides, withFoods, sidesForSlot, weekMealsWithProposals, fanSeed, slotMeals, fanRows, t]);

  // Compute stats for WeekSummaryLine
  const stats = useMemo(() => {
    const result = { recipe: 0, dining: 0, note: 0, skip: 0 };
    // Count every dish, not just the primary one — a slot can hold several.
    slotMeals.forEach((meals) => meals.forEach((meal) => {
      if (isRecipeMeal(meal)) {
        result.recipe++;
      } else if (isEntryMeal(meal)) {
        if (meal.entry?.type === 'dining') result.dining++;
        else if (meal.entry?.type === 'note') result.note++;
        else if (meal.entry?.type === 'skip') result.skip++;
        // leftover entries are not counted in stats display
      }
    }));
    return result;
  }, [slotMeals]);

  const moveSourceMeal = useMemo(
    () => (moveCtx ? weekMeals.find((m) => m.id === moveCtx.mealId) ?? null : null),
    [moveCtx, weekMeals]
  );

  const moveTitle = useMemo(
    () => (moveSourceMeal && recipeMap[moveSourceMeal.recipeId]?.title) || t('thisWeek.thisMeal'),
    [moveSourceMeal, recipeMap, t]
  );

  const selRecipe = detailFor ? (recipeMap[detailFor] ?? null) : null;
  // The meal's drawer is open: « Voir la recette » is one click away (#874).
  useEffect(() => prefetchRecipe(detailFor), [detailFor]);
  const selMeal = detailFor
    ? weekMeals.find((m) => m.recipeId === detailFor) ?? null
    : null;
  const selRating = selRecipe ? (recipeMetadata[selRecipe.id]?.rating ?? 0) : 0;

  // Library entries
  // The recipe hovered in the Library drawer, previewed in the slot it would fill.
  const [libPreviewId, setLibPreviewId] = useState<string | null>(null);

  const handleMenuPick = useCallback((key: MenuKey) => {
    const sk = slotMenu!.sk;
    setSlotMenu(null);
    const scheduledFor = addDays(weekStart, dayOf(sk));
    const mealType = (['breakfast', 'lunch', 'dinner'] as const)[rowOf(sk)];
    const currentMeal = slotsMap.get(sk);

    if (key === 'recipe') {
      dispatch(setLibFor(sk));
    } else if (key === 'dining' || key === 'note') {
      const initial = currentMeal ? entryInitialFor(currentMeal, key) : null;
      setEntryForm({ sk, type: key, initial });
    } else if (key === 'leftover') {
      setLeftoverPick({ sk });
    } else if (key === 'skip') {
      mutateWithUndo(t('thisWeek.toastNothingPlanned'), () =>
        dispatch(setSlotEntry({ entry: { type: 'skip' }, scheduledFor, mealType }))
      );
    } else if (key === 'clear') {
      mutateWithUndo(t('thisWeek.toastSlotCleared'), () =>
        dispatch(clearSlot({ scheduledFor, mealType }))
      );
    }
  }, [slotMenu, weekStart, dispatch, mutateWithUndo, slotsMap, t]);

  const handleEntrySave = useCallback(
    (data: { place?: string; note?: string; text?: string }) => {
      const { sk, type } = entryForm!;
      setEntryForm(null);
      const scheduledFor = addDays(weekStart, dayOf(sk));
      const mealType = (['breakfast', 'lunch', 'dinner'] as const)[rowOf(sk)];
      const entry = type === 'dining'
        ? { type: 'dining' as const, place: data.place || '', ...(data.note ? { note: data.note } : {}) }
        : { type: 'note' as const, text: data.text! };
      const undoMsg = placedInSlotMessage(
        t,
        sk,
        entry.type === 'dining' ? entry.place || t('entryForm.defaultPlace') : entry.text,
      );
      mutateWithUndo(undoMsg, () => dispatch(setSlotEntry({ entry, scheduledFor, mealType })));
    },
    [entryForm, weekStart, dispatch, mutateWithUndo, t]
  );

  const handleLeftoverPick = useCallback(
    (c: LeftoverCandidate) => {
      const sk = leftoverPick!.sk;
      setLeftoverPick(null);
      mutateWithUndo(placedInSlotMessage(t, sk, t('entry.leftovers')), () =>
        placeLeftover(
          c,
          addDays(weekStart, dayOf(sk)),
          (['breakfast', 'lunch', 'dinner'] as const)[rowOf(sk)]
        )
      );
    },
    [leftoverPick, weekStart, mutateWithUndo, placeLeftover, t]
  );

  const handleSlotPress = useCallback((sk: number) => {
    if (moving) return;
    dispatch(setSel(sel === sk ? null : sk));
  }, [dispatch, moving, sel]);

  const handleEmptyPress = useCallback((_sk: number, _anchor: Anchor) => {
    // Clicking "+" on an empty slot jumps straight to the 5-suggestion fan
    // (ReplaceDrawer). Entry types (Resto/Restes/Note/Rien) live there as
    // secondary actions, so the old SlotMenu chooser is no longer used here.
    dispatch(setSel(_sk));
  }, [dispatch]);

  // Breakfast, lunch and dinner of each day: free, full or over. Drives the
  // day headers' « Ajouter » and the drawer's meal switch.
  const dayMealStates = useMemo(
    () => [0, 1, 2, 3, 4, 5, 6].map((di) => mealStatesForDay(slotMeals, weekStart, di, nowAnchor())),
    [slotMeals, weekStart],
  );

  const handleAddToDay = useCallback((di: number) => {
    const mi = defaultMealForDay(dayMealStates[di]);
    if (mi === null) return;
    trackEvent('planner_day_add_pressed', { meal: MEALS[mi].mealType });
    handleFocusRow(mi as FocusRow, 'add');
    dispatch(setSel(slotKey(mi, di)));
  }, [dayMealStates, handleFocusRow, dispatch]);

  const handleSwitchMeal = useCallback((mi: number) => {
    if (sel === null) return;
    trackEvent('planner_add_meal_switched', { from: MEALS[rowOf(sel)].mealType, to: MEALS[mi].mealType });
    handleFocusRow(mi as FocusRow, 'add');
    dispatch(setSel(slotKey(mi, dayOf(sel))));
  }, [sel, handleFocusRow, dispatch]);

  const handleEntryPress = useCallback((sk: number, anchor: Anchor) => {
    setSlotMenu({ sk, anchor });
  }, []);

  const handlePhotoPress = useCallback((sk: number) => {
    const meal = slotsMap.get(sk);
    if (!meal) return;
    dispatch(setDetailFor(meal.recipeId));
  }, [dispatch, slotsMap]);

  /**
   * Puts a recipe in a slot. When the slot already holds a dish, `replace`
   * swaps that dish out — addToWeek alone appends, which used to stack an
   * invisible second meal onto the slot (and onto the grocery list). `stack`
   * is that append made on purpose (#746): the dish stays and the new one is
   * served with it, drawn under the card, for the same number of people.
   */
  const placeRecipe = useCallback((sk: number, recipeId: string, mode: 'replace' | 'stack' = 'replace') => {
    const recipe = recipeMap[recipeId];
    if (!recipe) return;
    const scheduledFor = addDays(weekStart, dayOf(sk));
    const mealType = (['breakfast', 'lunch', 'dinner'] as const)[rowOf(sk)];
    const previous = slotsMap.get(sk);

    if (previous && mode === 'stack' && canAddDish(slotMeals.get(sk))) {
      mutateWithUndo(t('thisWeek.toastAdded', { title: recipe.title }), () => {
        dispatch(addToWeek({
          recipe: recipe as any,
          scheduledFor,
          mealType,
          // A note carries no portions of its own (it is stored as 1).
          ...(previous.servings != null && !isEntryMeal(previous) ? { servings: previous.servings } : {}),
        }));
      });
    } else if (previous) {
      mutateWithUndo(t('thisWeek.toastReplaced', { title: recipe.title }), () => {
        // Remove first: addToWeek refuses to go past MAX_MEALS_PER_SLOT.
        dispatch(removeFromWeek(previous.id));
        dispatch(addToWeek({
          recipe: recipe as any,
          scheduledFor,
          mealType,
          ...(previous.servings != null && !isEntryMeal(previous) ? { servings: previous.servings } : {}),
          // Inherit the stamp so the new dish keeps the replaced one's place.
          ...(previous.createdAt ? { createdAt: previous.createdAt } : {}),
          // Replacing destroys the row and mints a new id, so the cook has to
          // be re-passed or the swap silently un-assigns them.
          ...(previous.cookId ? { cookId: previous.cookId } : {}),
        }));
      });
    } else {
      mutateWithUndo(placedInSlotMessage(t, sk, recipe.title), () => {
        dispatch(addToWeek({ recipe: recipe as any, scheduledFor, mealType }));
      });
    }
  }, [dispatch, recipeMap, slotsMap, slotMeals, weekStart, mutateWithUndo, t]);

  /** Opens the drawer on a cell's accompaniments. */
  const openSides = useCallback((sk: number) => {
    setDrawerSides(true);
    dispatch(setSel(sk));
  }, [dispatch]);

  const sideForCell = useCallback(
    (sk: number) => topSideForSlot(slotMeals.get(sk), weekMealsWithProposals),
    [topSideForSlot, slotMeals, weekMealsWithProposals],
  );

  const handleAddSide = useCallback((sk: number, side: SideOption) => {
    trackEvent('side_dish_added', { surface: 'desktop_cell', angle: side.labelKey });
    placeRecipe(sk, side.recipe.id, 'stack');
  }, [placeRecipe]);

  const handleLibSelect = useCallback((recipeId: string) => {
    const targetSk = (libFor !== null && libFor >= 0) ? libFor : sel;
    if (targetSk === null) {
      // Opened from the header with no slot chosen: there is nowhere to put
      // it, so the pick opens the recipe instead of doing nothing.
      dispatch(setLibFor(null));
      dispatch(setDetailFor(recipeId));
      return;
    }
    placeRecipe(targetSk, recipeId, drawerSides ? 'stack' : 'replace');
    dispatch(setLibFor(null));
    dispatch(setSel(null));
  }, [dispatch, libFor, sel, placeRecipe, drawerSides]);

  const handleRemove = useCallback(() => {
    if (!selMeal) return;
    dispatch(removeFromWeek(selMeal.id));
    dispatch(setDetailFor(null));
  }, [dispatch, selMeal]);

  const handleDetailMove = useCallback(() => {
    if (!selMeal) return;
    dispatch(setDetailFor(null));
    setMoveCtx({ mealId: selMeal.id });
    dispatch(setMoving(true));
  }, [dispatch, selMeal]);

  const closeMove = useCallback(() => {
    setMoveCtx(null);
    dispatch(setMoving(false));
  }, [dispatch]);

  /**
   * "Planifier une 2ᵉ fois" from inside the move sheet — the dish stays where
   * it is and a second helping lands in the slot the user picked.
   */
  const handleMoveSheetAdd = useCallback((
    date: string,
    mealType: PlannedMeal['mealType'],
    action?: 'add' | 'move' | 'replace',
    fromMealId?: string,
    options?: { cooked?: boolean },
  ) => {
    if (!moveSourceMeal) return;
    const recipe = moveSourceMeal.recipe ?? recipeMap[moveSourceMeal.recipeId];
    if (!recipe) return;
    mutateWithUndo(t('thisWeek.toastAdded', { title: moveTitle }), () => {
      if (action === 'replace' && fromMealId) dispatch(removeFromWeek(fromMealId));
      dispatch(addToWeek({ recipe: recipe as any, scheduledFor: date, mealType, cooked: options?.cooked }));
    }, [getWeekStartForDate(date)]);
    closeMove();
  }, [moveSourceMeal, recipeMap, moveTitle, mutateWithUndo, dispatch, closeMove, t]);

  /**
   * The plan sheet resolved a tap. It never decides for the user what happens
   * to a slot that was already taken, so `action` carries their choice through
   * to the dispatch — the pill and the mutation cannot drift apart.
   */
  const handleMoveConfirm = useCallback((
    mealId: string,
    date: string,
    mealType: PlannedMeal['mealType'],
    action: MoveAction = 'move',
  ) => {
    const sourceMeal = weekMeals.find((m) => m.id === mealId);
    if (!sourceMeal) return;

    const plan = buildMovePlan(sourceMeal, { scheduledFor: date, mealType }, action);
    if (!plan) return;

    const srcTitle = recipeMap[sourceMeal.recipeId]?.title ?? t('thisWeek.thisMeal');
    // Another week's Wednesday needs its date to be identifiable — "moved to
    // Wednesday" alone would read as this week's.
    const dayName = t(dayFullKey(dayIndexOf(plan.target.scheduledFor)));
    const day = plan.crossWeek
      ? t('thisWeek.dayWithDate', {
        day: dayName,
        date: formatDayMonth(plan.target.scheduledFor, i18n.language, 'short'),
      })
      : dayName;
    // Name what actually happened. The user picked "Remplacer" — a toast that
    // says "déplacé" hides the dish that was just evicted.
    const displaced = selectMealsForDateRange(store.getState(), date, date)
      .filter((m) => m.mealType === mealType && m.id !== mealId);
    const otherTitle = displaced[0]
      ? (recipeMap[displaced[0].recipeId]?.title ?? displaced[0].recipe?.title ?? t('thisWeek.thisMeal'))
      : t('thisWeek.thisMeal');

    const msg = action === 'swap'
      ? t('thisWeek.swappedWith', { recipe: srcTitle, day })
      : action === 'stack'
        ? t('thisWeek.stackedInto', { recipe: srcTitle, day })
        : action === 'replace'
          ? t('thisWeek.replacedOn', { recipe: srcTitle, other: otherTitle, day })
          : t('thisWeek.movedTo', { recipe: srcTitle, day });

    // A cross-week move rewrites two week buckets, so undo has to snapshot both
    // — restoring only the source week would leave a copy in the destination.
    mutateWithUndo(msg, () => {
      if (action === 'swap') {
        dispatch(swapSlots({ a: plan.source, b: plan.target }));
      } else {
        // Replace empties the target first, so the dish lands in a free slot —
        // the one shape moveMeal never refuses.
        if (action === 'replace') dispatch(clearSlot(plan.target));
        dispatch(moveMeal({
          mealId: sourceMeal.id,
          newScheduledFor: plan.target.scheduledFor,
          newMealType: plan.target.mealType,
        }));
      }
    }, plan.affectedWeeks);

    // The flash highlights a slot of the grid on screen; a slot key from
    // another week would light up the wrong card.
    if (!plan.crossWeek) {
      const sk = slotKey(MI_BY_MEAL_TYPE[mealType] ?? 1, dayIndexOf(date));
      dispatch(setFlash(sk));
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = setTimeout(() => {
        dispatch(setFlash(null));
      }, 900) as unknown as NodeJS.Timeout;
    }
    closeMove();
  }, [weekMeals, recipeMap, mutateWithUndo, dispatch, closeMove, t, i18n.language, store]);

  const handleDetailShuffle = useCallback(() => {
    if (!selMeal) return;
    dispatch(setDetailFor(null));
    // Derive the slot from the meal itself — the detail drawer can open from a
    // plain card click, where no slot is selected yet.
    const di = ((selMeal.dayOfWeek ?? 0) + 6) % 7;
    const mi = ({ breakfast: 0, lunch: 1, dinner: 2 } as const)[selMeal.mealType] ?? 1;
    dispatch(setSel(mi * 7 + di));
  }, [dispatch, selMeal]);

  const handleExpandRecipe = useCallback(() => {
    if (!selRecipe) return;
    dispatch(setDetailFor(null));
    openRecipeDetail(router, selRecipe);
  }, [dispatch, router, selRecipe]);

  const handleCookedToggle = useCallback(() => {
    if (!selMeal) return;
    if (selMeal.status === 'cooked') {
      dispatch(unmarkMealAsCooked({ mealId: selMeal.id }));
    } else {
      dispatch(markMealAsCooked({ mealId: selMeal.id }));
    }
  }, [dispatch, selMeal]);

  // The drawer's stars are a display of the derived household rating; tapping
  // them asks the table rather than overwriting the number behind their backs.
  const handleRate = useCallback(() => {
    if (!selRecipe) return;
    openVerdict({
      recipeId: selRecipe.id,
      recipeTitle: selRecipe.title,
      surface: 'cooked_stamp',
    });
  }, [openVerdict, selRecipe]);

  // Open the full "Comment c'était ?" capture (stars + date + note) for the
  // selected planned meal. Keeps the detail drawer open behind the modal.
  const handleOpenCookCapture = useCallback(() => {
    if (selMeal) setCookCaptureMeal(selMeal);
  }, [selMeal]);

  const handleCookCaptureSave = useCallback((entry: CookCaptureEntry) => {
    if (!cookCaptureMeal) return;
    // Anchor the cook stamp to the chosen day at noon (avoids UTC day-shift).
    const cookDate = new Date(entry.date + 'T12:00:00').toISOString();
    dispatch(markMealAsCooked({ mealId: cookCaptureMeal.id, cookedAt: cookDate, note: entry.note, noteBy: myUid ?? undefined }));
    if (entry.rating > 0) {
      // Legacy per-meal rating, so the recipe-detail cook card still shows
      // stars. The canonical per-recipe rating is NOT written here any more —
      // it is derived from the household's verdicts, which the sheet wrote as
      // they were tapped.
      dispatch(rateMeal({ mealId: cookCaptureMeal.id, rating: entry.rating }));
    }
    setCookCaptureMeal(null);
  }, [dispatch, cookCaptureMeal, myUid]);

  // A verdict answered in the sheet, then the sheet closed without saving: the
  // cook said they ate it (#865). Mark the meal cooked on the sheet's day, with
  // no note, so a note already on the meal is kept. A meal already cooked keeps
  // its own stamp.
  const handleCookCaptureDismissAnswered = useCallback(({ date }: { date: string }) => {
    if (!cookCaptureMeal) return;
    if (cookCaptureMeal.status !== 'cooked') {
      const cookDate = new Date(date + 'T12:00:00').toISOString();
      dispatch(markMealAsCooked({ mealId: cookCaptureMeal.id, cookedAt: cookDate }));
      dispatch(setToast({ text: t('cookLog.cookedOnDismiss') }));
      trackEvent('cook_capture_dismissed_cooked', { surface: 'desktop', date });
    }
  }, [dispatch, cookCaptureMeal, t]);

  const handleRemoveSlot = useCallback((sk: number) => {
    const meal = slotsMap.get(sk);
    if (!meal) return;
    mutateWithUndo(t('thisWeek.toastRemoved', { title: recipeMap[meal.recipeId]?.title ?? t('thisWeek.meal') }), () =>
      dispatch(removeFromWeek(meal.id))
    );
  }, [dispatch, mutateWithUndo, slotsMap, recipeMap, t]);

  const handleRemoveExtra = useCallback((meal: PlannedMealType) => {
    mutateWithUndo(t('thisWeek.toastRemoved', { title: recipeMap[meal.recipeId]?.title ?? t('thisWeek.meal') }), () =>
      dispatch(removeFromWeek(meal.id))
    );
  }, [dispatch, mutateWithUndo, recipeMap, t]);

  const handleCardDrop = useCallback((srcSk: number, targetSk: number) => {
    if (srcSk === targetSk) return;
    // Defensive: reject drops on excluded cells (weekday breakfast)
    if (isExcludedFromDrop(targetSk)) return;
    const a = { scheduledFor: addDays(weekStart, dayOf(srcSk)), mealType: MEALS[rowOf(srcSk)].mealType };
    const b = { scheduledFor: addDays(weekStart, dayOf(targetSk)), mealType: MEALS[rowOf(targetSk)].mealType };
    const src = slotsMap.get(srcSk);
    const tgt = slotsMap.get(targetSk);
    const srcLabel = src && isRecipeMeal(src) ? (recipeMap[src.recipeId]?.title ?? t('thisWeek.thisMeal')) : t('thisWeek.thisMeal');
    const day = t(dayFullKey(dayOf(targetSk)));
    const msg = tgt
      ? t('thisWeek.swappedWith', { recipe: srcLabel, day })
      : t('thisWeek.movedTo', { recipe: srcLabel, day });
    mutateWithUndo(msg, () => dispatch(swapSlots({ a, b })));
    dispatch(setFlash(targetSk));
    if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    flashTimeoutRef.current = setTimeout(() => {
      dispatch(setFlash(null));
    }, 900) as unknown as NodeJS.Timeout;
    dispatch(setSel(null));
  }, [weekStart, slotsMap, recipeMap, mutateWithUndo, dispatch]);

  const handleToastDismiss = useCallback(() => { dispatch(clearToast()); }, [dispatch]);

  const fanDi = sel !== null ? dayOf(sel) : 0;
  const thisWeekStart = currentWeekStart();
  const weekSubtitle = formatWeekRange(weekStart, i18n.language);

  return (
    <View style={styles.container} testID="weekly-desktop">
      {/* Horizontal header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Text style={styles.headerTitle}>{t('navigation.myMeals')}</Text>
          {weekMeals.length > 0 && sel === null && (
            <>
              <WeekSummaryLine stats={stats} />
              <Text style={styles.headerHint}>
                {/* HTML5 drag never fires on touch, so on a tablet the drag
                    wording tells the user to do something impossible (#646). */}
                {t(canHover() ? 'thisWeek.desktopHint' : 'thisWeek.desktopHintTouch')}
              </Text>
            </>
          )}
        </View>
        <View style={styles.headerRight}>
          <Pressable style={styles.settingsBtn} onPress={() => dispatch(setConfigPhase('config'))}>
            <Feather name="sliders" size={13} color={Paprika.textSecondary} />
            <Text style={styles.settingsBtnText}>{t('thisWeek.editSettings')}</Text>
          </Pressable>
          {/* An empty week keeps this control and changes its name: the
              800ms auto-generate that used to fill it is gone, and this layout
              had no other way to ask for a plan. */}
          <Pressable
            style={styles.settingsBtn}
            onPress={generateWeek}
            testID={weekMeals.length > 0 ? 'week-regenerate' : 'week-generate'}
            accessibilityRole="button"
            accessibilityLabel={weekMeals.length > 0 ? t('thisWeek.regenerate') : t('thisWeek.generateMyWeek')}
          >
            <Feather name={weekMeals.length > 0 ? 'refresh-cw' : 'zap'} size={13} color={Paprika.textSecondary} />
            <Text style={styles.settingsBtnText}>
              {weekMeals.length > 0 ? t('thisWeek.regenerate') : t('thisWeek.generateMyWeek')}
            </Text>
          </Pressable>
          <Pressable style={styles.libBtn} onPress={() => dispatch(setLibFor(-1))}>
            <Text style={styles.libBtnText}>{t('thisWeek.library')}</Text>
          </Pressable>
          {/* The range was already here; the arrows were two unnamed glyphs.
              Now they carry a role, a name and a testID — same control, findable. */}
          <View style={styles.weekNav} accessibilityLabel={t('thisWeek.weekOf', { range: weekSubtitle })}>
            <Pressable
              style={styles.navBtn}
              onPress={() => dispatch(setCurrentWeek(shiftWeek(weekStart, -1)))}
              accessibilityRole="button"
              accessibilityLabel={t('thisWeek.previousWeek')}
              testID="week-prev"
            >
              <Text style={styles.navBtnText}>‹</Text>
            </Pressable>
            <Text style={styles.weekRange} testID="week-range">{weekSubtitle}</Text>
            <Pressable
              style={styles.navBtn}
              onPress={() => dispatch(setCurrentWeek(shiftWeek(weekStart, 1)))}
              accessibilityRole="button"
              accessibilityLabel={t('thisWeek.nextWeek')}
              testID="week-next"
            >
              <Text style={styles.navBtnText}>›</Text>
            </Pressable>
          </View>
          {/* Only on another week: the way back is otherwise a count of ‹ clicks. */}
          {weekStart !== thisWeekStart && (
            <Pressable
              style={styles.todayBtn}
              onPress={() => dispatch(setCurrentWeek(thisWeekStart))}
              accessibilityRole="button"
              testID="week-today"
            >
              <Feather name="crosshair" size={13} color="#ffffff" />
              <Text style={styles.todayBtnText}>{t('thisWeek.backToToday')}</Text>
            </Pressable>
          )}
        </View>
      </View>

      {/* The band above the grid answers "what am I cooking?" when there is an
          answer (#579). The grocery total it replaced was the loudest thing on
          the screen and the least acted on from here — it survives as a chip
          inside the bar. With no next meal (week done, or another week on
          screen) the full banner comes back. */}
      {barEntry ? (
        <View style={styles.nextMealBarWrap}>
          <NextEntryBar
            entry={barEntry.meal.entry!}
            resolveTitle={(id) => recipeMap[id]?.title}
            targetDi={barEntry.di}
            todayDi={barEntry.todayDi}
            mi={barEntry.mi}
            onChange={(anchor) => handleEntryPress(barEntry.sk, anchor)}
          />
        </View>
      ) : nextMeal ? (
        <View style={styles.nextMealBarWrap}>
          <NextMealBar
            recipe={nextMeal.recipe}
            targetDi={nextMeal.di}
            todayDi={nextMeal.todayDi}
            mi={nextMeal.mi}
            servings={nextMeal.meal.servings}
            onPress={() => dispatch(setDetailFor(nextMeal.meal.recipeId))}
            onCook={() => openRecipeDetail(router, nextMeal.recipe)}
          />
        </View>
      ) : (
        <View style={styles.counterBannerWrap}>
          {/* "Des restes ?", once per cook (#663). Driven by store state rather
              than by the five markMealAsCooked call sites. */}
          {captureMeal && (
            <LeftoverCapturePrompt
              meal={captureMeal}
              title={recipeMap[captureMeal.recipeId]?.title ?? captureMeal.freeText ?? ''}
            />
          )}
          {pendingLeftovers.length > 0 && (
            <LeftoverPendingBanner
              pending={pendingLeftovers}
              resolveTitle={(id) => recipeMap[id]?.title}
            />
          )}
          <WeekCounterBanner />
        </View>
      )}

      {/* Once, after onboarding: the profile read back, correctable in one tap. */}
      <View style={styles.readingCard}>
        <ReadingCard weekStart={weekStart} weekMeals={weekMeals} />
      </View>

      {/* Main grid area */}
      <View style={styles.main}>
        <SlotGrid
          weekStart={weekStart}
          slotsMap={slotsMap}
          slotMeals={slotMeals}
          recipeIndex={recipeMap}
          sel={sel}
          moving={moving}
          flash={flash}
          menuFor={slotMenu?.sk}
          nextSk={nextMeal?.sk ?? null}
          onSlotPress={handleSlotPress}
          onPhotoPress={handlePhotoPress}
          onEmptyPress={handleEmptyPress}
          onEntryPress={handleEntryPress}
          onRemoveSlot={handleRemoveSlot}
          onRemoveExtra={handleRemoveExtra}
          onCardDrop={handleCardDrop}
          onExtraPress={(extra) => dispatch(setDetailFor(extra.recipeId))}
          sideFor={sideForCell}
          onAddSide={handleAddSide}
          onMoreSides={openSides}
          focusRow={focusRow}
          onFocusRow={handleFocusRow}
          canAddToDay={(di) => defaultMealForDay(dayMealStates[di]) !== null}
          onAddToDay={handleAddToDay}
          placeTarget={libFor !== null && libFor >= 0
            ? { sk: libFor, preview: libPreviewId ? (recipeMap[libPreviewId] ?? null) : null }
            : null}
        />

        {/* Replace drawer — shown when a recipe slot is selected and we have options */}
        {sel !== null && !moving && !libFor && !detailFor && (() => {
          const selMealSlot = slotsMap.get(sel);
          // Show the fan for an empty slot ("+") or a recipe slot ("Remplacer").
          // Entry-holding slots use the SlotMenu (handleEntryPress) instead —
          // except for the accompaniments of a note or leftovers.
          const isEmpty = !selMealSlot;
          const entrySides = drawerSides && !!selMealSlot && isSideableEntryMeal(selMealSlot);
          return (isEmpty || isRecipeMeal(selMealSlot!) || entrySides) && (
            <ReplaceDrawer
              sk={sel}
              currentRecipe={selMealSlot ? recipeMap[selMealSlot.recipeId] : undefined}
              options={fanOptions}
              profile={DAY_PROFILE[fanDi] as typeof DAY_PROFILE[0]}
              sides={drawerSides && !isEmpty}
              sideFoods={drawerSides && !isEmpty ? sideFoods : undefined}
              chipBar={fanChoice.chipBar}
              mealStates={isEmpty ? dayMealStates[dayOf(sel)] : undefined}
              onSwitchMeal={isEmpty ? handleSwitchMeal : undefined}
              onPickRecipe={(rid) => {
                const recipe = recipeMap[rid];
                if (!recipe) return;
                const scheduledFor = addDays(weekStart, dayOf(sel));
                const mealType = (['breakfast', 'lunch', 'dinner'] as const)[rowOf(sel)];
                const slotMealsArray = slotMeals.get(sel) ?? [];
                if (!drawerSides) fanChoice.trackPick(rid);

                // An accompaniment is served with the dish, in any household:
                // it competes with nothing, so there is nothing to propose.
                if (drawerSides && !isEmpty) {
                  const option = fanOptions.find((o) => o.recipe.id === rid);
                  trackEvent('side_dish_added', {
                    surface: 'desktop_drawer',
                    angle: option?.reason?.labelKey ?? (withFoods ? 'picked_foods' : null),
                  });
                  placeRecipe(sel, rid, 'stack');
                } else if (householdStatus === 'paired' && slotHasAcceptedMeal(slotMealsArray) && myUid) {
                  dispatch(proposeMeal({
                    recipe: recipe as any,
                    scheduledFor,
                    mealType,
                    uid: myUid,
                    proposesInsteadOf: selMealSlot?.id,
                  }));
                } else {
                  // Empty slot or solo household: place (replace) normally
                  placeRecipe(sel, rid);
                }

                dispatch(setSel(null));
                dispatch(setFlash(sel));
                if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
                flashTimeoutRef.current = setTimeout(() => {
                  dispatch(setFlash(null));
                }, 900) as unknown as NodeJS.Timeout;
              }}
              onConvert={(kind) => {
                const sk = sel;
                dispatch(setSel(null));
                if (kind === 'dining' || kind === 'note') {
                  setEntryForm({ sk, type: kind, initial: null });
                } else if (kind === 'leftover') {
                  setLeftoverPick({ sk });
                } else {
                  mutateWithUndo(t('thisWeek.toastNothingPlanned'), () =>
                    dispatch(setSlotEntry({
                      entry: { type: 'skip' },
                      scheduledFor: addDays(weekStart, dayOf(sk)),
                      mealType: (['breakfast', 'lunch', 'dinner'] as const)[rowOf(sk)],
                    }))
                  );
                }
              }}
              onOpenLibrary={() => dispatch(setLibFor(sel))}
              onRemove={() => {
                if (!selMealSlot) return;
                mutateWithUndo(t('thisWeek.toastRemoved', { title: recipeMap[selMealSlot.recipeId]?.title ?? t('thisWeek.meal') }), () =>
                  dispatch(removeFromWeek(selMealSlot.id))
                );
                dispatch(setSel(null));
              }}
              onOpenDetail={() => { if (selMealSlot) dispatch(setDetailFor(selMealSlot.recipeId)); }}
              onClose={() => dispatch(setSel(null))}
            />
          );
        })()}

        {/* Slot menu popover */}
        {slotMenu && (
          <SlotMenu
            anchor={slotMenu.anchor}
            hasContent={!!slotsMap.get(slotMenu.sk)}
            onPick={handleMenuPick}
            onClose={() => setSlotMenu(null)}
          />
        )}

        {/* Toast */}
        {toast && (
          <Toast
            toast={toast}
            onUndo={undo}
            onDismiss={handleToastDismiss}
          />
        )}
      </View>

      {/* Recipe detail drawer */}
      {!!detailFor && selRecipe && (
        <RecipeDetailDrawer
          recipe={selRecipe}
          dietaryRole={selMeal?.dietaryRole}
          onClose={() => dispatch(setDetailFor(null))}
          onRemove={handleRemove}
          onShuffle={handleDetailShuffle}
          onMove={selMeal ? handleDetailMove : undefined}
          onExpand={handleExpandRecipe}
          isCooked={selMeal?.status === 'cooked'}
          onCookedToggle={handleCookedToggle}
          rating={selRating}
          onRate={handleRate}
          onRateRecipe={selMeal ? handleOpenCookCapture : undefined}
        />
      )}

      {/* Move: the same plan sheet the recipe detail uses, so a move is one
          act with one vocabulary wherever it starts. */}
      {moveCtx && moving && moveSourceMeal && (
        <AddToMealPlanModal
          visible={moving}
          recipeId={String(moveSourceMeal.recipeId)}
          recipeTitle={moveTitle}
          mode="move"
          movingMealId={moveSourceMeal.id}
          onMove={handleMoveConfirm}
          onRemove={(id) => {
            mutateWithUndo(
              t('thisWeek.toastRemoved', { title: moveTitle }),
              () => dispatch(removeFromWeek(id)),
            );
            closeMove();
          }}
          onAdd={handleMoveSheetAdd}
          onClose={closeMove}
        />
      )}

      {/* Cook-capture sheet — stars + planned date + note, for the selected meal */}
      {cookCaptureMeal && (
        <CookCapture
          key={cookCaptureMeal.id}
          visible
          recipeId={cookCaptureMeal.recipeId}
          defaultDate={cookCaptureMeal.scheduledFor}
          initialNote={cookCaptureMeal.status === 'cooked' ? cookCaptureMeal.cookNote : undefined}
          onClose={() => setCookCaptureMeal(null)}
          onSave={handleCookCaptureSave}
          onDismissAnswered={handleCookCaptureDismissAnswered}
          t={t}
          language={i18n.language}
        />
      )}

      {/* Library drawer */}
      {libFor !== null && (
        <LibraryDrawer
          entries={recipeEntries}
          placingFor={libFor >= 0 ? libFor : null}
          weekStart={weekStart}
          onSelect={handleLibSelect}
          onPreview={setLibPreviewId}
          onClose={() => dispatch(setLibFor(null))}
        />
      )}

      {/* Entry form modal */}
      {entryForm && (
        <EntryFormModal
          type={entryForm.type}
          initial={entryForm.initial}
          dayLabel={slotLabel(t, entryForm.sk)}
          onSave={handleEntrySave}
          onCancel={() => setEntryForm(null)}
        />
      )}

      {/* Leftover picker modal */}
      {leftoverPick && (
        <LeftoverPickerModal
          fridge={leftoverFridge}
          freezer={leftoverFreezer}
          today={today}
          dayLabel={slotLabel(t, leftoverPick.sk)}
          resolve={(id: string) => recipeMap[id]}
          onPick={handleLeftoverPick}
          onCancel={() => setLeftoverPick(null)}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  readingCard: { paddingHorizontal: 24, maxWidth: 720 },
  container: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: Paprika.background,
  },
  counterBannerWrap: {
    marginHorizontal: 24,
    marginBottom: 4,
    maxWidth: 520,
  },
  // The next-meal bar is horizontal: photo, title, grocery chip and CTA share
  // one row, so it needs the full width the 520px banner cap would deny it.
  nextMealBarWrap: {
    marginHorizontal: 24,
    marginBottom: 4,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 12,
  },
  headerLeft: {
    flex: 1,
    gap: 12,
  },
  headerTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 28,
    lineHeight: 34,
    color: Paprika.textPrimary,
  },
  headerHint: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textMuted,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  settingsBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1.5,
    borderColor: Paprika.border,
    borderRadius: 100,
    paddingVertical: 8,
    paddingHorizontal: 14,
    backgroundColor: Paprika.surfaceLowest,
  },
  settingsBtnText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
  libBtn: {
    backgroundColor: Paprika.brandTint,
    borderRadius: 100,
    paddingVertical: 9,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  libBtnText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    color: Paprika.primary,
  },
  weekNav: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginLeft: 6,
  },
  weekRange: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textSecondary,
    minWidth: 110,
    textAlign: 'center',
    textTransform: 'capitalize',
  },
  navBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: Paprika.border,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Paprika.surfaceLowest,
  },
  navBtnText: {
    fontFamily: 'Inter-Regular',
    fontSize: 18,
    color: Paprika.textSecondary,
  },
  todayBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 7,
    paddingHorizontal: 13,
    borderRadius: 100,
    backgroundColor: Paprika.primary,
  },
  todayBtnText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12.5,
    color: '#ffffff',
  },
  main: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
  },
});
